import { ConversationStatus, hasActiveConversation } from "@/app/api/message";
import { initSocket } from "@/app/hooks/useClientInit";
import { webSocketService } from "@/app/services/WebSocketService";
import { useMessageStore } from "@/app/store";
import { CurrentChatStatus, SocketConnectionStatus } from "@/app/store/types";

const CONNECT_TIMEOUT_MS = 20000;

/**
 * 消息重发前确保 WebSocket 可用：
 * - 已连接则直接 true
 * - 否则先 hasActiveConversation，再按 issueTypeId / URL group_id 调 initSocket 建连
 * - 失败时兜底 handleReconnectOffline
 */
export async function ensureWebSocketReadyForResend(): Promise<boolean> {
  if (webSocketService.isConnected()) return true;

  try {
    const sessionInfo = await hasActiveConversation();
    const store = useMessageStore.getState();
    store.setSessionInfo(sessionInfo);

    if (sessionInfo.hasActiveConversation) {
      if (sessionInfo.status === ConversationStatus.LineUp) {
        store.setCurrentChatStatus(CurrentChatStatus.LineUp);
      } else {
        store.setCurrentChatStatus(CurrentChatStatus.Online);
      }
    }

    const issueTypeId = sessionInfo.hasActiveConversation
      ? sessionInfo.issueTypeId
      : typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("group_id") ||
          store.sessionInfo?.issueTypeId ||
          ""
        : store.sessionInfo?.issueTypeId || "";

    if (!issueTypeId) {
      return false;
    }

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(
        () => reject(new Error("websocket connect timeout")),
        CONNECT_TIMEOUT_MS,
      );
      initSocket({ id: issueTypeId }, (status) => {
        if (status === SocketConnectionStatus.CONNECTED) {
          window.clearTimeout(timer);
          resolve();
          return;
        }
        // initSocket 在 getSocketTicket 失败时会直接回调 ERROR，若不处理会空等 CONNECT_TIMEOUT_MS
        if (status === SocketConnectionStatus.ERROR) {
          window.clearTimeout(timer);
          reject(new Error("websocket init: ticket or socket unavailable"));
        }
      });
    });

    return webSocketService.isConnected();
  } catch {
    webSocketService.handleReconnectOffline();
    return webSocketService.isConnected();
  }
}
